import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/Shared";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDateTime } from "@/lib/format";
import { GitBranch, StopCircle, CheckCircle2, Clock, Send } from "lucide-react";

const STEP_ICON = { Sent: CheckCircle2, Scheduled: Clock, Failed: StopCircle, Skipped: StopCircle };
const STEP_COLOR = { Sent: "#0B7C5A", Scheduled: "#D97706", Failed: "#F83A84", Skipped: "#6B7189" };

export function EnquiryCadences({ enquiryId }) {
  const [data, setData] = useState(null);
  const [pick, setPick] = useState("");
  const load = useCallback(async () => setData((await api.get(`/enquiries/${enquiryId}/cadences`)).data), [enquiryId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const active = data.enrollments.find((e) => e.status === "Active");

  const apply = async () => {
    if (!pick) return;
    try { await api.post(`/cadences/${pick}/apply`, { enquiry_id: enquiryId }); toast.success("Cadence applied"); setPick(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const stop = async (id) => {
    try { await api.post(`/cadence-enrollments/${id}/stop`); toast.success("Cadence stopped"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Card className="p-4" data-testid="enq-cadences">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-[#6B7189] flex items-center gap-1.5"><GitBranch className="w-3.5 h-3.5" /> WhatsApp cadence</div>
        {!active && data.available.length > 0 && (
          <div className="flex items-center gap-2">
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="apply-cadence-select"><SelectValue placeholder="Apply cadence" /></SelectTrigger>
              <SelectContent>{data.available.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" className="h-8 bg-[#0B7C5A] hover:bg-[#09664a]" disabled={!pick} onClick={apply} data-testid="apply-cadence-btn"><Send className="w-3.5 h-3.5" /> Apply</Button>
          </div>
        )}
      </div>

      {active ? (
        <div data-testid="active-enrollment">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[#101D51]">{active.cadence_name}</span>
            <Button size="sm" variant="outline" className="h-8 text-[#F83A84] border-[#F83A84]/40" onClick={() => stop(active.id)} data-testid="stop-cadence-btn"><StopCircle className="w-3.5 h-3.5" /> Stop</Button>
          </div>
          <div className="mt-2 space-y-1">
            {active.steps.map((s, i) => {
              const Icon = STEP_ICON[s.status] || Clock;
              return (
                <div key={s.step_id} className="flex items-center gap-2 text-xs">
                  <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: STEP_COLOR[s.status] }} />
                  <span className="text-[#6B7189]">Step {i + 1}</span>
                  <span className="text-[#101D51]">{s.template_name || "—"}</span>
                  <span className="text-[#9AA0B5] ml-auto">{s.status === "Scheduled" ? formatDateTime(s.scheduled_at) : s.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-sm text-[#6B7189]">
          {data.available.length === 0 ? "No active cadences available. Create one in Settings → Cadences."
            : data.enrollments.length > 0 ? `Last run: ${data.enrollments[0].status}${data.enrollments[0].stopped_reason ? ` (${data.enrollments[0].stopped_reason})` : ""}.`
              : "Not enrolled in any cadence."}
        </div>
      )}
    </Card>
  );
}
